import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Caption, getTextLanguage } from "../../../types";
import classNames from "./ImproveTranslationDialog.module.css";
import { Room as LivekitRoom } from "livekit-client";
import api from "../../../hooks/api";
import { useAxios } from "../../../hooks/axios";
import { toast } from "react-toastify";
import RadixDialog from "./RadixDialog";
import Button from "../Button";

type ImproveTranslationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  livekitRoom: LivekitRoom;
  caption: Caption;
  language: string;
};

function ImproveTranslationDialog(props: ImproveTranslationDialogProps) {
  const language = useMemo(() => {
    return getTextLanguage(props.language)!;
  }, [props.language]);

  const [correctTranslation, setCorrectTranslation] = useState(
    props.caption.translations[language]
  );

  useEffect(() => {
    setCorrectTranslation(props.caption.translations[language]);
  }, [props.caption, language]);

  const axios = useAxios();
  const sendCorrectTranslationRequest = api.useCorrectTranslation(axios);

  const submit = useCallback(async () => {
    try {
      await sendCorrectTranslationRequest({
        id: props.livekitRoom.name,
        caption: props.caption,
        correct_translation: correctTranslation,
        language: language,
      });
      toast(
        <div className="Toast SuccessToast">
          <div>{"Successfully submitted"}</div>
        </div>,
        { type: "success" }
      );
      props.onOpenChange(false);
    } catch (error) {
      toast(
        <div className="Toast ErrorToast">
          {"Something went wrong, please try again"}
        </div>,
        {
          type: "error",
        }
      );
      throw error;
    }
  }, [sendCorrectTranslationRequest, props, correctTranslation, language]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!textAreaRef.current) {
      return;
    }
    textAreaRef.current.style.height = `${Math.max(
      textAreaRef.current.scrollHeight,
      36
    )}px`;
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        if (event.target === textAreaRef.current) {
          event.preventDefault();
        }
        submit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [submit]);

  return (
    <RadixDialog
      title={"Report this translation"}
      open={props.open}
      onOpenChange={props.onOpenChange}
      content={
        <div className={classNames.ImproveTranslationDialog}>
          <div className={classNames.CorrectTranslation}>
            <h3>Correct translation</h3>
            <textarea
              ref={textAreaRef}
              value={correctTranslation}
              onChange={(e) => setCorrectTranslation(e.target.value)}
            />
          </div>

          <div className={classNames.ImproveTranslationButtons}>
            <Button onClick={submit}>Submit</Button>
          </div>

          <div className={classNames.Disclaimer}>
            Translations are AI-generated and may be incorrect. By confirming to
            submit the translation inaccuracy, you acknowledge that you have
            read and agree to the data collection disclaimer{" "}
            <a
              href={
                "https://byrdhouse.notion.site/Privacy-Policy-c71b7c579187429da97f8aec36b1585e"
              }
              target="_blank"
            >
              here
            </a>
            .
          </div>
        </div>
      }
    />
  );
}

export default ImproveTranslationDialog;
